import {
  Component,
  input,
  output,
  computed,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { NavigationItem } from '@fc-core/navbar/navigation/navigation-item';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { NavigationChildrenComponent } from './navigation-children.component';
import { NavigationPopoverComponent } from './navigation-popover.component';
import { includes } from 'lodash-es';
import { Store } from '@ngrx/store';
import { selectRouteUrl } from '@fc-store/selectors';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';
import { NgClass } from '@angular/common';
import { OverlayHoverDirective } from '@fc-shared/ui/fc-overlay/overlay-hover.directive';
import { OverlayContentDirective } from '@fc-shared/ui/fc-overlay/overlay-content.directive';
import { BadgeComponent } from '@fc-shared/ui/badge/badge.component';

@Component({
  selector: 'fc-navigation-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    IconComponent,
    RouterModule,
    NavigationChildrenComponent,
    NavigationPopoverComponent,
    TooltipDirective,
    NgClass,
    OverlayHoverDirective,
    OverlayContentDirective,
    BadgeComponent,
  ],
  templateUrl: './navigation-item.component.html',
  styles: [
    `
      @layer navbar-animations {
        @media (prefers-reduced-motion: reduce) {
          .transition-\[grid-template-rows\] {
            transition: none !important;
          }
        }
      }
    `,
  ],
  host: { class: 'w-full flex flex-col' },
})
export class NavigationItemComponent {
  store = inject(Store);
  router = inject(Router);
  link = input.required<NavigationItem>();
  isCollapsed = input<boolean>();
  toggle = output<NavigationItem>();
  onClick = output();
  activeRoute = this.store.selectSignal(selectRouteUrl);
  hasInner = computed(() => !!this.link()?.inner?.length);

  linkActive = computed(() => {
    const currentUrl = this.activeRoute();

    if (this.hasInner()) {
      return this.link().inner!.some((child) =>
        includes(currentUrl, child.link!),
      );
    }

    return includes(currentUrl, this.link().link!);
  });

  parentActive = computed(
    () => this.linkActive() && (this.isCollapsed() || !this.link().open),
  );

  activeIcon = computed(() =>
    this.linkActive() ? this.link().iconActive : this.link().icon,
  );

  parentActiveIcon = computed(() =>
    this.parentActive() ? this.link().iconActive : this.link().icon,
  );

  handleParentClick(event?: Event) {
    event?.stopPropagation();

    if (this.isCollapsed()) {
      event?.preventDefault();
      this.router.navigate([this.link().inner?.[0]?.link]);
      return;
    }

    this.toggle.emit(this.link());
  }
}
